import { isEmpty } from "lodash";
import { Eye, EyeOff } from "lucide-react";
import { ComponentPropsWithoutRef, useState } from "react";
import { Control, FieldPath, FieldValues, RegisterOptions } from "react-hook-form";

import { Button } from "../../../components/ui/common/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../components/ui/common/form";
import { Input } from "../../../components/ui/common/input";
import { cn } from "../../../lib/utils";

interface PasswordFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<ComponentPropsWithoutRef<typeof Input>, "name" | "type"> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  required?: boolean;
  errorMessage?: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
}

/**
 * `FormInput`'s (`src/components/ui/Input/FormInput/FormInput.tsx`) sibling
 * for password fields: the same shadcn Form primitives and validation
 * contract, plus a show/hide toggle `FormInput` has no slot for.
 */
export function PasswordField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required = false,
  errorMessage,
  rules,
  className,
  onBlur,
  ...inputProps
}: PasswordFieldProps<TFieldValues, TName>) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      rules={{
        required: required ? errorMessage || "This field is required" : false,
        ...rules
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && !isEmpty(label) && <span className="tw-text-destructive tw-ml-0.5">*</span>}
          </FormLabel>
          <div className="tw-relative">
            <FormControl>
              <Input
                type={isVisible ? "text" : "password"}
                className={cn("tw-pr-10", className)}
                {...field}
                {...inputProps}
                onBlur={event => {
                  field.onBlur();
                  onBlur?.(event);
                }}
              />
            </FormControl>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="tw-absolute tw-right-1 tw-top-1 tw-h-8 tw-w-8 tw-text-muted-foreground"
              aria-label={isVisible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
              aria-pressed={isVisible}
              disabled={inputProps.disabled}
              onClick={() => setIsVisible(visible => !visible)}
            >
              {isVisible ? <EyeOff className="tw-h-4 tw-w-4" /> : <Eye className="tw-h-4 tw-w-4" />}
            </Button>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
